import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import PersonAppKennungen from '@/components/app/PersonAppKennungen';
import BulkAppKennungen from '@/components/app/BulkAppKennungen';
import EmailLink from '@/components/EmailLink';
import {
  AlertCircle,
  CheckCircle2,
  CreditCard,
  Users,
  Wand2,
  Download,
} from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../../components/layout/Layout';
import RecordNavigator from '../../components/ui/record-navigator';
import SearchBuilder, { applyFilters } from '../../components/search/SearchBuilder';
import api from '@/lib/axios';
import { writeSelectedRecord } from '@/lib/selectedRecord';
import { footerToParams, getFooterSettings } from '@/components/FooterDateBar';
import DateInput from '@/components/ui/date-input';
import { normalizeIban, lookupIbanInfo } from '@/lib/iban';
import { accountHolderLabel, payerHeading, payerNameLabel } from '@/lib/utils';
import useListKeyboardNav, { LIST_ROW_CLASS } from '@/hooks/useListKeyboardNav'
import useTableColumns from '@/hooks/useTableColumns';
import useTableSort from '@/hooks/useTableSort';
import SortableTh from '@/components/table/SortableTh';

const GUARDIAN_SORT_ACCESSORS = {
  name: (g) => `${g.last_name || ''} ${g.first_name || ''}`.trim(),
  email: (g) => g.email || g.phone,
  students: (g) => (g.student_names || []).join(', '),
  iban: (g) => g.bank_iban,
  mandate: (g) => g.sepa_mandate_reference,
  status: (g) => g.mandate_status_label || g.mandate_status || (g.has_sepa_data ? 'active' : ''),
};

// Define filterable fields for guardians
const FILTER_FIELDS = [
  { key: 'first_name', label: 'Vorname', type: 'text' },
  { key: 'last_name', label: 'Nachname', type: 'text' },
  { key: 'email', label: 'E-Mail', type: 'text' },
  { key: 'phone', label: 'Telefon', type: 'text' },
  { key: 'city', label: 'Ort', type: 'text' },
  { key: 'postal_code', label: 'PLZ', type: 'text' },
  { key: 'has_sepa_data', label: 'SEPA-Daten', type: 'select', options: [
    { value: 'true', label: 'Vollständig' },
    { value: 'false', label: 'Unvollständig' },
  ]},
  { key: 'mandate_status', label: 'Mandat-Status', type: 'select', options: [
    { value: 'active',     label: '🟢 Aktiv' },
    { value: 'issued',     label: '🔵 Erteilt' },
    { value: 'expired',    label: '🔴 Erloschen (>36 Mo.)' },
    { value: 'revoked',    label: '⚪ Widerrufen' },
    { value: 'incomplete', label: '🟡 Unvollständig' },
    { value: 'no_mandate', label: '⚫ Kein Mandat' },
  ]},
  { key: 'student_count', label: 'Schüleranzahl', type: 'number' },
];

// Define columns with visibility
const DEFAULT_COLUMNS = [
  { key: 'name', label: 'Name', visible: true },
  { key: 'email', label: 'E-Mail/Telefon', visible: true },
  { key: 'students', label: 'Schüler', visible: true },
  { key: 'iban', label: 'IBAN', visible: true },
  { key: 'mandate', label: 'Mandatsreferenz', visible: true },
  { key: 'status', label: 'Status', visible: true },
];

const GuardiansPage = () => {
  const [allGuardians, setAllGuardians] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState([]);
  const { columns, setColumns } = useTableColumns('guardians-list', DEFAULT_COLUMNS);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedGuardian, setSelectedGuardian] = useState(null);
  const [cardFilter, setCardFilter] = useState('all'); // 'all' | 'with_sepa' | 'without_sepa'
  const [editForm, setEditForm] = useState({
    bank_iban: '',
    bank_bic: '',
    sepa_mandate_reference: '',
    sepa_mandate_date: '',
    bank_account_holder: '',
    sepa_debit_day: 1
  });
  const [bicLookupHint, setBicLookupHint] = useState('');
  const ibanLookupTimer = useRef(null);

  const [searchParams] = useSearchParams();
  const [footerTick, setFooterTick] = useState(0);
  useEffect(() => {
    const onFooter = () => setFooterTick((n) => n + 1);
    window.addEventListener('footer-settings-changed', onFooter);
    return () => window.removeEventListener('footer-settings-changed', onFooter);
  }, []);

  const handleIbanChange = (raw) => {
    const bank_iban = raw.toUpperCase();
    setEditForm((prev) => ({ ...prev, bank_iban }));
    setBicLookupHint('');
    if (ibanLookupTimer.current) clearTimeout(ibanLookupTimer.current);
    const cleaned = normalizeIban(bank_iban);
    if (cleaned.length < 15) return;
    ibanLookupTimer.current = setTimeout(async () => {
      const info = await lookupIbanInfo(cleaned);
      if (!info?.bic) {
        setBicLookupHint(info ? '' : (cleaned.length >= 22 ? 'BIC konnte nicht ermittelt werden' : ''));
        return;
      }
      setEditForm((prev) => {
        if (normalizeIban(prev.bank_iban) !== cleaned) return prev;
        return { ...prev, bank_bic: info.bic };
      });
      setBicLookupHint(info.bank_name ? `Bank: ${info.bank_name}` : 'aus IBAN ermittelt');
    }, 350);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [footerTick]);

  // Apply ?mandate_status=expired URL parameter (deep-link from overview dashboard)
  useEffect(() => {
    const ms = searchParams.get('mandate_status');
    if (ms && !filters.find((f) => f.field === 'mandate_status')) {
      setFilters((prev) => [...prev, { field: 'mandate_status', operator: 'equals', value: ms }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const gid = searchParams.get('guardian');
    if (gid) openEditDialog({ id: gid });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (selectedGuardian?.id) writeSelectedRecord({ guardianId: selectedGuardian.id });
  }, [selectedGuardian?.id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch stats
      const statsRes = await api.get('/guardians/stats');
      setStats(statsRes.data);
      
      // Load all guardians in batches
      let allData = [];
      let offset = 0;
      const batchSize = 100;
      
      const period = footerToParams(getFooterSettings());
      while (true) {
        const response = await api.get('/guardians', {
          params: { limit: batchSize, offset, ...period },
        });
        const batch = response.data;
        if (!batch || batch.length === 0) break;
        allData = [...allData, ...batch];
        if (batch.length < batchSize) break;
        offset += batchSize;
      }
      
      setAllGuardians(allData);
      setCurrentIndex(0);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const { sort, toggleSort, sortRows } = useTableSort(GUARDIAN_SORT_ACCESSORS, { key: 'name' });

  // Apply filters client-side for instant feedback
  const filteredGuardians = useMemo(() => {
    const searchFields = ['first_name', 'last_name', 'email', 'phone', 'city'];
    let result = applyFilters(allGuardians, filters, searchTerm, searchFields);
    
    // Convert has_sepa_data boolean to string for comparison
    result = result.map(g => ({
      ...g,
      has_sepa_data_str: String(g.has_sepa_data)
    }));
    
    // Apply has_sepa_data filter specially since it's boolean
    const sepaFilter = filters.find(f => f.field === 'has_sepa_data' && f.value);
    if (sepaFilter) {
      result = result.filter(g => String(g.has_sepa_data) === sepaFilter.value);
    }

    // Apply mandate_status filter (server-rendered status code, client-filtered)
    const mandateFilter = filters.find(f => f.field === 'mandate_status' && f.value);
    if (mandateFilter) {
      result = result.filter(g => g.mandate_status === mandateFilter.value);
    }

    // Apply card-based quick filter
    if (cardFilter === 'with_sepa') {
      result = result.filter(g => g.has_sepa_data);
    } else if (cardFilter === 'without_sepa') {
      result = result.filter(g => !g.has_sepa_data);
    }

    return sortRows(result);
  }, [allGuardians, filters, searchTerm, cardFilter, sortRows]);

  const handleNavigate = (newIndex) => {
    setCurrentIndex(newIndex);
    const row = document.querySelector(`[data-testid="guardian-row-${filteredGuardians[newIndex]?.id}"]`);
    if (row) {
      row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      row.classList.add('bg-blue-50');
      setTimeout(() => row.classList.remove('bg-blue-50'), 2000);
    }
  };

  const openEditDialog = async (guardian) => {
    try {
      const res = await api.get(`/guardians/${guardian.id}`);
      const fullData = res.data;
      setSelectedGuardian(fullData);
      setEditForm({
        bank_iban: fullData.bank_iban || '',
        bank_bic: fullData.bank_bic || '',
        sepa_mandate_reference: fullData.sepa_mandate_reference || '',
        sepa_mandate_date: fullData.sepa_mandate_date ? fullData.sepa_mandate_date.split('T')[0] : '',
        bank_account_holder: (() => {
          const label = accountHolderLabel(
            fullData.bank_account_holder,
            fullData.first_name,
            fullData.last_name,
          );
          return label === '—' ? '' : label;
        })(),
        sepa_debit_day: fullData.sepa_debit_day || 1
      });
      setBicLookupHint('');
      setShowEditDialog(true);
      // Vorhandene IBAN ohne BIC → BIC nachträglich ableiten
      if (fullData.bank_iban && !fullData.bank_bic) {
        lookupIbanInfo(fullData.bank_iban).then((info) => {
          if (!info?.bic) return;
          setEditForm((prev) => ({ ...prev, bank_bic: info.bic }));
          setBicLookupHint(info.bank_name ? `Bank: ${info.bank_name}` : 'aus IBAN ermittelt');
        });
      } else if (fullData.bank_iban && fullData.bank_bic) {
        // Falsche/veraltete BIC korrigieren, wenn Lookup abweicht
        lookupIbanInfo(fullData.bank_iban).then((info) => {
          if (!info?.bic) return;
          const current = normalizeIban(fullData.bank_bic);
          if (info.bic !== current) {
            setEditForm((prev) => ({ ...prev, bank_bic: info.bic }));
            setBicLookupHint(
              info.bank_name
                ? `Bank: ${info.bank_name} (BIC aus IBAN aktualisiert)`
                : 'BIC aus IBAN aktualisiert'
            );
          }
        });
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    }
  };

  const { getRowProps } = useListKeyboardNav({
    items: filteredGuardians,
    onSelect: (g) => openEditDialog(g),
    enabled: !showEditDialog,
  });

  const saveGuardian = async () => {
    try {
      const updateData = {
        ...editForm,
        sepa_mandate_date: editForm.sepa_mandate_date ? new Date(editForm.sepa_mandate_date).toISOString() : null
      };
      
      await api.put(`/guardians/${selectedGuardian.id}`, updateData);
      toast.success('SEPA-Daten gespeichert');
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const generateMandateReferences = async () => {
    if (!window.confirm('Für alle Zahlungspflichtigen ohne Mandatsreferenz eine neue generieren?')) return;
    
    try {
      const res = await api.post('/guardians/generate-mandate-references?prefix=SEPA');
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Generieren');
    }
  };

  const exportLetterCsv = async () => {
    // Pull active mandate_status from filters (array of {field, value}); default = all if none set
    const params = {};
    const mandateFilter = filters.find((f) => f.field === 'mandate_status' && f.value);
    if (mandateFilter) params.mandate_status = mandateFilter.value;
    try {
      const res = await api.get('/guardians/export/letter-csv', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const tag = mandateFilter?.value || 'alle';
      a.download = `mandate-brief-export_${tag}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const rows = res.headers?.['x-rows-written'] || '?';
      toast.success(`Brief-CSV heruntergeladen (${rows} Empfänger)`);
    } catch (err) {
      toast.error('CSV-Export fehlgeschlagen');
    }
  };

  const isColumnVisible = (key) => {
    const col = columns.find(c => c.key === key);
    return col ? col.visible : true;
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="guardians-page">
        <div className="flex justify-between items-center gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-1">Zahlungspflichtige</h1>
            <p className="text-slate-600">
              {filteredGuardians.length} von {allGuardians.length} Zahlungspflichtigen
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
          <BulkAppKennungen personType="guardian" audienceLabel="Zahlungspflichtige" />
          <Button
            variant="outline"
            onClick={generateMandateReferences}
            data-testid="generate-mandates-btn"
          >
            <Wand2 className="w-4 h-4 mr-2" />
            Mandatsreferenzen generieren
          </Button>
          <Button
            variant="outline"
            onClick={exportLetterCsv}
            className="text-brand-red border-brand-red/30 hover:bg-brand-red/5"
            data-testid="export-letter-csv-btn"
            title="Aktuelle Filterung als Serienbrief-CSV exportieren (Excel-DE)"
          >
            <Download className="w-4 h-4 mr-2" />
            Brief-CSV
          </Button>
          </div>
        </div>

        {/* Stats Cards — clickable filters */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button
              type="button"
              onClick={() => setCardFilter(cardFilter === 'all' ? 'all' : 'all')}
              className={`text-left transition-all ${cardFilter === 'all' ? 'ring-2 ring-blue-500 ring-offset-1' : 'hover:shadow-md'}`}
              data-testid="filter-card-all"
            >
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Users className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Zahlungspflichtige</p>
                      <p className="text-2xl font-bold">{stats.payers}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </button>

            <button
              type="button"
              onClick={() => setCardFilter(cardFilter === 'with_sepa' ? 'all' : 'with_sepa')}
              className={`text-left transition-all ${cardFilter === 'with_sepa' ? 'ring-2 ring-green-500 ring-offset-1' : 'hover:shadow-md'}`}
              data-testid="filter-card-with-sepa"
            >
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Mit SEPA-Daten</p>
                      <p className="text-2xl font-bold text-green-600">{stats.with_complete_sepa}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </button>

            <button
              type="button"
              onClick={() => setCardFilter(cardFilter === 'without_sepa' ? 'all' : 'without_sepa')}
              className={`text-left transition-all ${cardFilter === 'without_sepa' ? 'ring-2 ring-orange-500 ring-offset-1' : 'hover:shadow-md'}`}
              data-testid="filter-card-without-sepa"
            >
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <AlertCircle className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Ohne SEPA-Daten</p>
                      <p className="text-2xl font-bold text-orange-600">{stats.without_sepa}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </button>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-brand-red/10 rounded-lg">
                    <CreditCard className="w-5 h-5 text-brand-red" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">SEPA-Abdeckung</p>
                    <p className="text-2xl font-bold">{stats.sepa_coverage_percent}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {cardFilter !== 'all' && (
          <div className="flex items-center gap-2 text-sm text-slate-600 px-1" data-testid="active-card-filter">
            <span>Aktiver Filter:</span>
            <span className={`font-semibold ${cardFilter === 'with_sepa' ? 'text-green-700' : 'text-orange-700'}`}>
              {cardFilter === 'with_sepa' ? 'Nur mit SEPA-Daten' : 'Nur ohne SEPA-Daten'}
            </span>
            <button onClick={() => setCardFilter('all')} className="text-brand-red hover:underline ml-1" data-testid="clear-card-filter">
              zurücksetzen
            </button>
          </div>
        )}

        {/* Progress Bar */}
        {stats && stats.payers > 0 && (
          <Card className="bg-gradient-to-r from-brand-red/5 to-slate-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">SEPA-Daten Fortschritt</span>
                <span className="text-sm text-gray-600">
                  {stats.with_complete_sepa} von {stats.payers}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-brand-red/50 to-slate-500 h-3 rounded-full transition-all"
                  style={{ width: `${stats.sepa_coverage_percent}%` }}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* SearchBuilder */}
        <SearchBuilder
          fields={FILTER_FIELDS}
          onFilterChange={setFilters}
          onSearch={setSearchTerm}
          columns={columns}
          onColumnsChange={setColumns}
          placeholder="Zahlungspflichtige suchen (Name, E-Mail, Ort)..."
          className="mb-4"
        />

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <span className="text-sm text-slate-600">
              {filteredGuardians.length} Ergebnisse
            </span>
            
            {filteredGuardians.length > 0 && (
              <RecordNavigator
                currentIndex={currentIndex}
                totalCount={filteredGuardians.length}
                onNavigate={handleNavigate}
                records={filteredGuardians}
                getRecordLabel={(g) => payerNameLabel(g.last_name, g.first_name)}
              />
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" data-testid="guardians-table">
              <thead className="bg-slate-50 text-slate-500 font-medium text-xs uppercase tracking-wider border-b border-slate-200">
                <tr>
                  {DEFAULT_COLUMNS.filter((col) => isColumnVisible(col.key)).map((col) => (
                    <SortableTh
                      key={col.key}
                      sortKey={col.key}
                      sort={sort}
                      onSort={toggleSort}
                      testIdPrefix="guardians-sort"
                    >
                      {col.label}
                    </SortableTh>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-slate-500">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-red"></div>
                      </div>
                    </td>
                  </tr>
                ) : filteredGuardians.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-slate-500">
                      Keine Zahlungspflichtigen gefunden
                    </td>
                  </tr>
                ) : (
                  filteredGuardians.map((g, idx) => (
                    <tr 
                      key={g.id} 
                      data-testid={`guardian-row-${g.id}`}
                      className={LIST_ROW_CLASS}
                      {...getRowProps(idx, g)}
                    >
                      {isColumnVisible('name') && (
                        <td className="p-4 text-sm text-slate-700 font-medium">
                          {payerNameLabel(g.last_name, g.first_name)}
                        </td>
                      )}
                      {isColumnVisible('email') && (
                        <td className="p-4 text-sm">
                          <div>
                            {g.email && <div className="text-slate-600"><EmailLink email={g.email} /></div>}
                            {g.phone && <div className="text-slate-500 text-xs">{g.phone}</div>}
                            {!g.email && !g.phone && <span className="text-slate-400">-</span>}
                          </div>
                        </td>
                      )}
                      {isColumnVisible('students') && (
                        <td className="p-4 text-sm text-slate-600">
                          {g.student_names?.slice(0, 2).map((name, i) => (
                            <div key={i}>{name}</div>
                          ))}
                          {g.student_names?.length > 2 && (
                            <div className="text-slate-400 text-xs">+{g.student_names.length - 2} weitere</div>
                          )}
                        </td>
                      )}
                      {isColumnVisible('iban') && (
                        <td className="p-4 text-sm font-mono text-slate-600">
                          {g.bank_iban || <span className="text-slate-400">-</span>}
                        </td>
                      )}
                      {isColumnVisible('mandate') && (
                        <td className="p-4 text-sm font-mono text-slate-600">
                          {g.sepa_mandate_reference || <span className="text-slate-400">-</span>}
                        </td>
                      )}
                      {isColumnVisible('status') && (
                        <td className="p-4 text-sm">
                          {(() => {
                            const status = g.mandate_status || (g.has_sepa_data ? 'active' : 'no_mandate');
                            const meta = {
                              active:     { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',  emoji: '🟢' },
                              issued:     { cls: 'bg-sky-50 text-sky-700 border-sky-200',              emoji: '🔵' },
                              expired:    { cls: 'bg-rose-50 text-rose-700 border-rose-200',           emoji: '🔴' },
                              revoked:    { cls: 'bg-slate-100 text-slate-700 border-slate-200',       emoji: '⚪' },
                              incomplete: { cls: 'bg-amber-50 text-amber-700 border-amber-200',        emoji: '🟡' },
                              no_mandate: { cls: 'bg-slate-50 text-slate-500 border-slate-200',        emoji: '⚫' },
                            }[status] || { cls: 'bg-slate-50 text-slate-500 border-slate-200', emoji: '?' };
                            const tooltip = g.mandate_last_used_at
                              ? `Letzte Nutzung: ${g.mandate_last_used_at}`
                              : (status === 'issued' ? 'Mandat erteilt – Erstlastschrift erwartet'
                                : status === 'expired' ? 'Erloschen nach 36 Monaten ohne Nutzung — bitte neu unterzeichnen'
                                : '');
                            return (
                              <span className="inline-flex items-center gap-1.5">
                                <Badge className={`${meta.cls} text-xs`} title={tooltip}
                                  data-testid={`mandate-status-${g.id}`}>
                                  <span className="mr-1">{meta.emoji}</span>
                                  {g.mandate_status_label || (g.has_sepa_data ? 'Vollständig' : 'Unvollständig')}
                                </Badge>
                                {g.has_sepa_data && (
                                  <Badge className="bg-slate-50 text-slate-600 border-slate-200 text-[10px]"
                                    title="Abbuchungstermin" data-testid={`debit-day-${g.id}`}>
                                    zum {String(g.sepa_debit_day || 1).padStart(2, '0')}.
                                  </Badge>
                                )}
                              </span>
                            );
                          })()}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {(selectedGuardian
                  && payerHeading(selectedGuardian.first_name, selectedGuardian.last_name))
                  || 'Zahlungspflichtige:r'}
              </DialogTitle>
            </DialogHeader>

            <Tabs defaultValue="sepa" className="w-full min-w-0">
              <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
                <TabsTrigger value="sepa" data-testid="guardian-tab-sepa" className="shrink-0">SEPA</TabsTrigger>
                <TabsTrigger value="app" data-testid="guardian-tab-app" className="shrink-0">App</TabsTrigger>
              </TabsList>

              <TabsContent value="app" className="mt-4">
                {selectedGuardian && (
                  <PersonAppKennungen personType="guardian" personId={selectedGuardian.id} />
                )}
              </TabsContent>

              <TabsContent value="sepa" className="mt-4">
            <div className="space-y-4">
              <div>
                <Label>Kontoinhaber</Label>
                <Input
                  value={editForm.bank_account_holder}
                  onChange={(e) => setEditForm({...editForm, bank_account_holder: e.target.value})}
                  placeholder="Name des Kontoinhabers"
                  data-testid="guardian-sepa-holder"
                />
              </div>
              
              <div>
                <Label>IBAN *</Label>
                <Input
                  value={editForm.bank_iban}
                  onChange={(e) => handleIbanChange(e.target.value)}
                  placeholder="DE89 3704 0044 0532 0130 00"
                  className="font-mono"
                  data-testid="guardian-sepa-iban"
                />
              </div>
              
              <div>
                <Label>BIC</Label>
                <Input
                  value={editForm.bank_bic}
                  onChange={(e) => {
                    setBicLookupHint('');
                    setEditForm({...editForm, bank_bic: e.target.value.toUpperCase()});
                  }}
                  placeholder="wird aus IBAN ermittelt"
                  className="font-mono"
                  data-testid="guardian-sepa-bic"
                />
                {bicLookupHint && (
                  <p className="text-xs text-slate-500 mt-1" data-testid="guardian-sepa-bic-hint">
                    {bicLookupHint}
                  </p>
                )}
              </div>
              
              <div>
                <Label>Mandatsreferenz *</Label>
                <Input
                  value={editForm.sepa_mandate_reference}
                  onChange={(e) => setEditForm({...editForm, sepa_mandate_reference: e.target.value})}
                  placeholder="SEPA-20260225-0001"
                  className="font-mono"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Eindeutige Referenznummer für das SEPA-Mandat
                </p>
              </div>
              
              <div>
                <Label>Mandatsdatum *</Label>
                <DateInput
                  
                  value={editForm.sepa_mandate_date}
                  onChange={(e) => setEditForm({...editForm, sepa_mandate_date: e.target.value})}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Datum der Mandatserteilung/Unterschrift
                </p>
              </div>

              <div>
                <Label>Abbuchungstermin</Label>
                <select
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  value={editForm.sepa_debit_day}
                  onChange={(e) => setEditForm({...editForm, sepa_debit_day: Number(e.target.value)})}
                  data-testid="guardian-debit-day-select"
                >
                  <option value={1}>zum 01. des Monats</option>
                  <option value={15}>zum 15. des Monats</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  An welchem Termin die Lastschrift eingezogen wird
                </p>
              </div>
            </div>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Abbrechen
              </Button>
              <Button 
                onClick={saveGuardian}
                disabled={!editForm.bank_iban || !editForm.sepa_mandate_reference || !editForm.sepa_mandate_date}
              >
                Speichern
              </Button>
            </DialogFooter>
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default GuardiansPage;
